import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Grid, Table, Tbody, Td, Text, Th, Thead, Tr, useMediaQuery } from '@chakra-ui/react';

import { TableHeader } from '../TableElements/tableHeader';
import { t } from 'i18next';
import { useMoralisCloudFunction, useMoralisQuery } from 'react-moralis';
import { ITransactionElementData, TransactionElement } from './transactionElement';
import usePaginationController from '../../providers/PaginationController/usePaginationController';
import useGames, { emptyGame, IGame } from '../../providers/Games/useGames';
import useUsers from 'src/providers/Users/useUsers';
import { emptyUser } from '../../providers/User/useUser';
import { CHAKRA_MAX_WIDTH_MOBILE, scrollColoredSmall } from 'src/utils/themer';
import { TransactionLoader } from '../../pages/HomePage/TransactionsView/transactionLoader';
import { Color } from 'src/theme/color';

export enum TransactionState {
	LOADING,
	FOUND,
}

export interface ITransaction {
	id: string;
	value: string;
	token: string;
	user: string;
	time: string;
	marketplace: string;
}

interface ITransactionsList {
	onClickRightHeader: () => void;
	header: string;
	headerRightText: string;
	headerStyle: any;
	filter?: {
		user?: {
			address: string;
		};
		game?: {
			address: string;
		};
	};
	customTheme?: /** User page does not have this list custom-themed */ IGame['colors'];
}

export const TransactionsList = ({
	header,
	headerStyle,
	onClickRightHeader,
	headerRightText,
	filter,
	customTheme,
}: ITransactionsList) => {
	const [state, handleState] = useState<TransactionState>(TransactionState.LOADING);
	const [transactions, setTransactions] = useState<ITransaction[]>([]);
	const [data, setData] = useState<ITransactionElementData[]>([]);

	const users = useUsers();
	const games = useGames();

	const [isReverseOrder, setIsReverseOrder] = useState(false);

	const [isMobile] = useMediaQuery(CHAKRA_MAX_WIDTH_MOBILE);

	const { currentPage, advancePage, elementsPerPage, elementsTotal } = usePaginationController(isMobile ? 4 : 10);

	const { data: listedGamesAddressesQueryData }: { data: any } = useMoralisCloudFunction('getListedGamesAddresses');

	/** TODO: Factor out into a provider, not necessarily caching state */
	const {
		data: queryTransactions,
		isLoading: isQueryTransactionsLoading,
		isFetching: isQueryTransactionsFetching,
	} = useMoralisQuery(
		'Bought',
		query => {
			if (filter) {
				if (filter.user) {
					query.equalTo('user', filter.user.address);
				}
				if (filter.game) {
					query.equalTo('license', filter.game.address);
				}
			}
			query.descending('createdAt').limit(currentPage * elementsPerPage);

			return query;
		},
		[currentPage, elementsPerPage]
	);

	const handleLoadMore = () => {
		setIsReverseOrder(!isMobile);
		advancePage();
	};

	useEffect(() => {
		if (isQueryTransactionsLoading || isQueryTransactionsFetching || queryTransactions.length === 0) {
			handleState(TransactionState.LOADING);
		} else {
			const payload = (queryTransactions as any).map((entity: any) => {
				const converted: ITransaction = {
					id: entity.attributes.transaction_hash,
					value: entity.attributes.amount,
					token: entity.attributes.license,
					marketplace: entity.attributes.marketplace ?? '0x714ef5c429ce9bdd0cac3631d30474bd04e954dc',
					user: entity.attributes.user ?? entity.attributes.referrer /** `referrer` is a V2 Fallback */,
					time: entity.attributes.block_timestamp,
				};
				return converted;
			}) as ITransaction[];
			if (isReverseOrder) {
				payload.reverse();
			}

			if (listedGamesAddressesQueryData && listedGamesAddressesQueryData.length) {
				const transactions = payload.filter(transaction =>
					listedGamesAddressesQueryData.includes(transaction.token)
				);

				users.fetch(transactions.map(tx => tx.user));
				games.fetch(transactions.map(tx => tx.token));
				setTransactions(transactions);
			}

			handleState(TransactionState.FOUND);
		}
	}, [queryTransactions, isQueryTransactionsLoading, isQueryTransactionsFetching, listedGamesAddressesQueryData]);

	/** Merge transactions with user data */
	useEffect(() => {
		const combined: typeof data = [];
		for (const entry of transactions) {
			const matchingGameData = games.data.find(element => element.address === entry.token);
			const matchingUserData = users.data.find(element => element.address === entry.user);

			combined.push({
				...entry /** data */,
				gameData: {
					media: matchingGameData?.media ?? emptyGame.media,
					name: matchingGameData?.name ?? emptyGame.name,
				},
				userData: {
					nickname: matchingUserData?.nickname ?? emptyUser.nickname,
				},
			});
		}

		setData(combined);
	}, [transactions, users.state, games.state]);

	const transactionComponents = useMemo(
		() =>
			data.map((transaction, index) => (
				<TransactionElement
					key={transaction.id}
					isBorder={index + 1 < data.length}
					data={transaction}
					customTheme={customTheme}
				/>
			)),
		[data, isReverseOrder]
	);

	return (
		<Flex
			gridGap="16px"
			flexDirection="column"
		>
			<TableHeader
				isLoadMore={true}
				onClickRightHeader={handleLoadMore}
				loading={state === TransactionState.LOADING}
				header={header}
				headerRightText={headerRightText}
				headerStyle={headerStyle}
				customTheme={customTheme}
			/>
			<Box
				display={{ base: 'none', hashup: 'flex' }}
				css={scrollColoredSmall(Color.BACKGROUND_LIGHT, customTheme?.actionColor ?? Color.ACTION)}
				overflowY="auto"
				maxHeight="calc(40px + 7 * 72px)"
				flexDirection="column"
			>
				<Table variant="simple">
					<Thead
						pos="sticky"
						top="0"
						zIndex={100}
						borderTopRadius="5px"
						bg={customTheme?.backgroundColor ?? Color.BACKGROUND}
					>
						<Tr
							userSelect="none"
							backdropFilter={customTheme ? 'brightness(90%)' : 'unset'}
						>
							<Th
								padding="0 14px"
								fontWeight="400"
								w="35px"
								border="none"
								color={customTheme?.textColor ?? Color.TEXT}
								borderTopLeftRadius="5px"
								textAlign="center"
								textTransform="none"
							>
								#
							</Th>
							<Th
								border="none"
								fontWeight="400"
								color={customTheme?.textColor ?? Color.TEXT}
								textTransform="none"
							>
								{t('games.type').toString()}
							</Th>
							<Th
								border="none"
								fontWeight="400"
								color={customTheme?.textColor ?? Color.TEXT}
								borderTopRightRadius="5px"
								textTransform="none"
							>
								{t('transactions.details').toString()}
							</Th>
						</Tr>
					</Thead>
					<Tbody
						bg={customTheme ? 'transparent' : 'backgroundSecondary'}
						justifyContent="center"
					>
						{transactions.length === 0 && state === TransactionState.FOUND ? (
							<Tr
								w="70vw"
								justifyContent="center"
								p="15px"
								display="flex"
							>
								<Td display="flex">{t('transactions.noTransactions').toString()}</Td>
							</Tr>
						) : (
							transactionComponents
						)}
					</Tbody>
				</Table>
				{state === TransactionState.LOADING && queryTransactions.length === 0 && (
					<Grid
						height="100%"
						pt="20px"
						width="100%"
						gridGap="10px"
						justifyContent="space-between"
						alignItems="center"
						templateColumns="repeat(1, 1fr)"
						templateRows="repeat(10, 1fr)"
					>
						{Array.from(Array(13).keys()).map(id => {
							return <TransactionLoader key={id} />;
						})}
					</Grid>
				)}
			</Box>
			<Table
				display={{ base: 'flex', hashup: 'none' }}
				background={customTheme ? 'transparent' : 'backgroundSecondary'}
				borderRadius="7px 7px 0 0"
				width="100%"
				flexDirection="column"
				position="relative"
			>
				<Tbody
					display="flex"
					flexDirection="column"
					bg="action"
				>
					{transactionComponents}
					<Tr
						display="flex"
						flexDirection="column"
						_after={{ display: 'none' }}
					>
						<Td
							borderColor="transparent"
							display="flex"
							flexDirection="column"
							padding="4px 10px 16px 10px"
							_after={{ display: 'none' }}
						>
							<Button
								variant="unstyled"
								borderRadius="4px"
								background="action"
								fontSize="12px"
								lineHeight="15px"
								padding="8px"
								textTransform="none"
								position="relative"
								display="flex"
								width="100%"
								onClick={handleLoadMore}
							>
								{useMemo(
									() =>
										state === TransactionState.LOADING ? (
											<TransactionLoader />
										) : (
											<Text>See more</Text>
										),
									[state]
								)}
							</Button>
						</Td>
					</Tr>
				</Tbody>
			</Table>
		</Flex>
	);
};
